
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLetsTalkModal, setShowLetsTalkModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    occupation: '',
    designation: '',
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.mobile) {
      setSubmitStatus('Please fill in required fields');
      return;
    }

    if (formData.comment.length > 500) {
      setSubmitStatus('Comment must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            email: formData.email,
            mobile: formData.mobile,
            occupation: formData.occupation,
            designation: formData.designation,
            comment: formData.comment,
            source: 'header_lets_talk'
          }
        ]);

      if (!error) {
        setSubmitStatus('Message sent successfully! We will contact you soon.');
        setFormData({
          email: '',
          mobile: '',
          occupation: '',
          designation: '',
          comment: ''
        });
        setTimeout(() => {
          setShowLetsTalkModal(false);
          setSubmitStatus('');
        }, 2000);
      } else {
        console.error('Supabase error:', error);
        setSubmitStatus('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('Failed to send message. Please try again.');
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-cipher-black border-b border-gray-800 py-4'
        : 'bg-transparent py-6'
        }`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center z-50 relative">
              <Link to="/">
                <img
                  src="/logo.png"
                  alt="Negotrix Logo"
                  className="h-6 sm:h-8 w-auto brightness-0 invert cursor-pointer"
                />
              </Link>
            </div>

            {/* Navigation - Hidden on Mobile, Visible on Tablet+ */}
            <nav className="hidden md:flex items-center space-x-6 lg:space-x-8 xl:space-x-12" role="navigation" aria-label="Main navigation">
               <Link
                to="/"
                className={`text-white hover:text-cipher-hypergreen transition-colors cursor-pointer text-xs sm:text-sm tracking-wide font-medium uppercase ${isActive('/') ? 'text-cipher-hypergreen' : ''}`}
              >
                Home
              </Link>
              <Link
                to="/cost-categories"
                className={`text-white hover:text-cipher-hypergreen transition-colors cursor-pointer text-xs sm:text-sm tracking-wide font-medium uppercase ${isActive('/cost-categories') ? 'text-cipher-hypergreen' : ''}`}
              >
                 Cost Categories
              </Link>
              <Link
                to="/how-it-works"
                className={`text-white hover:text-cipher-hypergreen transition-colors cursor-pointer text-xs sm:text-sm tracking-wide font-medium uppercase ${isActive('/how-it-works') ? 'text-cipher-hypergreen' : ''}`}
              >
                How It Works
              </Link>
              <Link
                to="/success-stories"
                className={`text-white hover:text-cipher-hypergreen transition-colors cursor-pointer text-xs sm:text-sm tracking-wide font-medium uppercase ${isActive('/success-stories') ? 'text-cipher-hypergreen' : ''}`}
              >
                Success Stories
              </Link>
              <Link
                to="/contact"
                className={`text-white hover:text-cipher-hypergreen transition-colors cursor-pointer text-xs sm:text-sm tracking-wide font-medium uppercase ${isActive('/contact') ? 'text-cipher-hypergreen' : ''}`}
              >
                Contact
              </Link>
            </nav>

            {/* CTA Button & Mobile Menu Toggle */}
            <div className="flex items-center space-x-4 sm:space-x-6 z-50 relative">
              <Link
                to="/contact"
                className="hidden md:inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm tracking-wide font-medium uppercase rounded-full text-cipher-black bg-cipher-hypergreen hover:bg-white transition-all duration-300"
              >
                Get in Touch
              </Link>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden text-white hover:text-cipher-hypergreen transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl sm:text-3xl`} aria-hidden="true"></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 z-40 bg-cipher-white transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        } md:hidden pt-20 pb-6 px-4 sm:px-6 overflow-y-auto border-l border-cipher-border`}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="flex flex-col h-full">
          <nav className="flex flex-col py-4" role="navigation" aria-label="Mobile navigation">
               <Link
                to="/"
                className={`text-cipher-black hover:text-cipher-hypergreen transition-colors cursor-pointer font-bold focus-ring rounded-md px-4 py-3 text-lg border-b border-cipher-border ${isActive('/') ? 'text-cipher-hypergreen' : ''
                  }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                 Home
               </Link>
               <Link
                 to="/cost-categories"
              className={`text-cipher-black hover:text-cipher-hypergreen transition-colors cursor-pointer font-bold focus-ring rounded-md px-4 py-3 text-lg border-b border-cipher-border ${isActive('/cost-categories') ? 'text-cipher-hypergreen' : ''
                }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
                 Cost Categories
            </Link>
            <Link
              to="/how-it-works"
              className={`text-cipher-black hover:text-cipher-hypergreen transition-colors cursor-pointer font-bold focus-ring rounded-md px-4 py-3 text-lg border-b border-cipher-border ${isActive('/how-it-works') ? 'text-cipher-hypergreen' : ''
                }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              How It Works
            </Link>
            <Link
              to="/success-stories"
              className={`text-cipher-black hover:text-cipher-hypergreen transition-colors cursor-pointer font-bold focus-ring rounded-md px-4 py-3 text-lg border-b border-cipher-border ${isActive('/success-stories') ? 'text-cipher-hypergreen' : ''
                }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Success Stories
            </Link>
            <Link
              to="/contact"
              className={`text-cipher-black hover:text-cipher-hypergreen transition-colors cursor-pointer font-bold focus-ring rounded-md px-4 py-3 text-lg border-b border-cipher-border ${isActive('/contact') ? 'text-cipher-hypergreen' : ''
                }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </nav>
          
          <div className="mt-auto pt-8 pb-4">
            <Link
              to="/contact"
              className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-full shadow-sm text-base font-bold text-cipher-black bg-cipher-hypergreen hover:bg-black hover:text-white transition-all focus-ring"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get in Touch
            </Link>
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>amit@negotrix.com</p>
              <p className="mt-1">+91 9540099199</p>
            </div>
          </div>
        </div>
      </div>
        </div>
      </header>

      {/* Let's Talk Modal */}
      {showLetsTalkModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowLetsTalkModal(false)}></div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleFormSubmit} data-readdy-form id="lets-talk-form">
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 sm:mx-0 sm:h-10 sm:w-10">
                      <i className="ri-chat-3-line text-green-600 dark:text-green-400 text-xl" aria-hidden="true"></i>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                        Let's Talk
                      </h3>

                      <div className="space-y-4">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email ID *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                            placeholder="your.email@company.com"
                          />
                        </div>

                        <div>
                          <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Mobile Number *
                          </label>
                          <input
                            type="tel"
                            id="mobile"
                            name="mobile"
                            required
                            value={formData.mobile}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>

                        <div>
                          <label htmlFor="occupation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Occupation
                          </label>
                          <input
                            type="text"
                            id="occupation"
                            name="occupation"
                            value={formData.occupation}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                            placeholder="Your occupation"
                          />
                        </div>

                        <div>
                          <label htmlFor="designation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Designation
                          </label>
                          <input
                            type="text"
                            id="designation"
                            name="designation"
                            value={formData.designation}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                            placeholder="Your designation"
                          />
                        </div>

                        <div>
                          <label htmlFor="comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Comment
                          </label>
                          <textarea
                            id="comment"
                            name="comment"
                            rows={3}
                            value={formData.comment}
                            onChange={handleInputChange}
                            maxLength={500}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm resize-none"
                            placeholder="Tell us about your requirements..."
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formData.comment.length}/500 characters
                          </p>
                        </div>

                        {submitStatus && (
                          <div className={`p-3 rounded-md text-sm ${submitStatus.includes('successfully')
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                            }`}>
                            {submitStatus}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors whitespace-nowrap"
                    onClick={() => setShowLetsTalkModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
